import api from '@Api/Api'
import { useSnackbar } from '@eduact/ed-components'
import { isApiResponse } from '@Features/Base/BaseApi/BaseApi'
import { handleUnauthorized } from '@Features/Base/BaseApi/handleUnauthorized'
import { isBaseError } from '@Features/Base/Error/BaseError'
import { SnackbarConfig } from '@Utilities/SnackbarConfig'
import React, { useEffect } from 'react'
import { useAppDispatch } from '@Hooks/ReduxHooks'
import { useNavigate } from 'react-router'

const ApiProvider: React.FC = ({ children }) => {
	const { openSnackbar } = useSnackbar()
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	useEffect(() => {
		// The request interceptor that used to live here is gone. It was a near-copy
		// of the one in Api.ts — same Bearer construction, same headers — registered
		// inside this effect, so every re-render stacked another copy on the same
		// axios client. Worse, it assigned `config.headers` wholesale and omitted
		// `x-embed`, so which headers a request actually carried depended on the
		// order the duplicates happened to run in. There is one interceptor now, in
		// Api.ts, and it no longer attaches a token at all.
		const responseInterceptor = api.interceptors.response.use(
			function (response) {
				const { data } = response
				if (isApiResponse(data) && data.show) {
					openSnackbar({ ...SnackbarConfig.success, text: data.message })
				}

				return Promise.resolve(response)
			},
			function (error) {
				// Auth failures first. This covers the raw-axios call sites — login,
				// signup, profile, devices, the upload helper — which bypass RTK
				// Query entirely and were otherwise completely 401-blind.
				//
				// `error.response.status`, NOT `error.status`: axios 1.2.1 builds an
				// AxiosError that assigns only message/name/code/config/request/
				// response. There is no top-level `status` at runtime (it arrived
				// upstream in 1.8), yet the bundled .d.ts declares `status?: number`
				// — so the wrong form type-checks cleanly and silently never fires.
				if (error?.response?.status === 401) {
					// Hand over the RESPONSE, not the AxiosError: handleUnauthorized
					// reads the envelope at `data.data.code`, which on an axios error
					// lives one level down.
					handleUnauthorized(dispatch, error.response)
					// Reject with the response too, matching the path below — every
					// downstream catch in this app is written against that shape
					// (`isBaseError(e) && e.data.message`).
					return Promise.reject(error.response)
				}
				if (isBaseError(error) && error.data.show) {
					openSnackbar({
						...SnackbarConfig.warning,
						text: error.data.message,
					})
				}
				return Promise.reject(error.response)
			}
		)

		// Ejected on cleanup. Without this the effect re-runs on every `openSnackbar`
		// identity change and leaves the previous interceptor attached, so one
		// response eventually fires several snackbars.
		return () => {
			api.interceptors.response.eject(responseInterceptor)
		}
	}, [openSnackbar])
	return <>{children}</>
}

export default ApiProvider
